// Translate Supabase Auth errors into operator-readable copy.
// Goals:
//   - Don't leak whether an email exists (user enumeration). Login and
//     password-reset failures collapse to a single generic message.
//   - Don't echo a Supabase stack trace into a pharmacy admin's screen.
//   - Keep PHI out of error text — emails are user input but order
//     numbers / patient names must never reach this surface.

export type AuthOperation = 'login' | 'signup' | 'reset-request' | 'set-password';

const GENERIC = {
  login: 'Email or password is incorrect.',
  signup: 'We could not create your account. Contact your pharmacy admin.',
  'reset-request': 'We could not send a reset email. Try again in a minute.',
  'set-password': 'We could not update your password. Try the link from your email again.',
} satisfies Record<AuthOperation, string>;

type SupabaseLikeError = {
  message?: string;
  status?: number;
  code?: string;
  name?: string;
};

function asError(err: unknown): SupabaseLikeError | null {
  if (err && typeof err === 'object') return err as SupabaseLikeError;
  return null;
}

export function friendlyAuthError(operation: AuthOperation, err: unknown): string {
  const e = asError(err);
  if (!e) return GENERIC[operation];

  const code = (e.code ?? '').toLowerCase();
  const message = (e.message ?? '').toLowerCase();

  if (code.includes('rate') || message.includes('rate limit')) {
    return 'Too many attempts. Wait a minute before trying again.';
  }

  if (operation === 'login') {
    // invalid_credentials, invalid_grant — both collapse to generic.
    return GENERIC.login;
  }

  if (operation === 'reset-request') {
    return GENERIC['reset-request'];
  }

  if (operation === 'signup') {
    if (message.includes('already registered') || code === 'user_already_exists') {
      // Already-registered emails should funnel to login or invite, not signup.
      return 'An account already exists for this email. Try logging in or contact your admin.';
    }
    if (message.includes('weak password') || code === 'weak_password') {
      return 'Password does not meet strength requirements.';
    }
    return GENERIC.signup;
  }

  if (operation === 'set-password') {
    if (message.includes('expired') || message.includes('invalid token')) {
      return 'This invite or reset link has expired. Request a new one.';
    }
    if (message.includes('weak password') || code === 'weak_password') {
      return 'Password does not meet strength requirements.';
    }
    return GENERIC['set-password'];
  }

  return GENERIC[operation];
}
